import type { ReactNode } from 'react';
import { AlertCircle, AlertTriangle, CheckCircle2, Info } from 'lucide-react';
import { cn } from '@/lib/utils';

export type BannerTone = 'info' | 'success' | 'warning' | 'danger';

const TONE_STYLES: Record<
  BannerTone,
  { container: string; icon: React.ComponentType<{ className?: string }> }
> = {
  info: { container: 'border-accent-100 bg-accent-50 text-accent-600', icon: Info },
  success: {
    container: 'border-success-100 bg-success-50 text-success-600',
    icon: CheckCircle2,
  },
  warning: {
    container: 'border-warning-100 bg-warning-50 text-warning-600',
    icon: AlertTriangle,
  },
  danger: {
    container: 'border-danger-100 bg-danger-50 text-danger-600',
    icon: AlertCircle,
  },
};

export function Banner({
  tone = 'info',
  title,
  children,
  className,
}: {
  tone?: BannerTone;
  title?: string;
  children: ReactNode;
  className?: string;
}) {
  const { container, icon: Icon } = TONE_STYLES[tone];
  return (
    <div
      role={tone === 'danger' ? 'alert' : 'status'}
      className={cn('flex gap-2 rounded-md border px-3 py-2 text-sm', container, className)}
    >
      <Icon className="mt-0.5 h-4 w-4 shrink-0" />
      <div className="flex-1">
        {title && <div className="font-semibold">{title}</div>}
        <div className={cn(title && 'mt-0.5 text-xs opacity-90')}>{children}</div>
      </div>
    </div>
  );
}
